import { useState } from "react";
import { Search, ChevronDown, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { trackGA4Event } from "@/lib/ga4";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { LocationTypeahead, LocationSelection } from "@/components/search/LocationTypeahead";

interface SearchBarProps {
  variant?: "hero" | "compact";
  className?: string;
}

// IDX Broker configuration
const IDX_BASE_URL = "https://homes.prpsj.com/idx/results/listings";
const IDX_ID = "b004";

// Property type mapping to IDX pt parameter
const PROPERTY_TYPES = [
  { label: "Any Type", value: "" },
  { label: "Residential", value: "1" },
  { label: "Residential Lease", value: "2" },
  { label: "Land", value: "3" },
  { label: "Commercial Lease", value: "4" },
  { label: "Commercial Sale", value: "5" },
  { label: "Farms", value: "6" },
  { label: "Multi Family", value: "7" },
  { label: "Business Opportunity", value: "8" },
];

// Status options for IDX a_propStatus[] parameter
const STATUS_OPTIONS = [
  { label: "Active", value: "ACTIVE", default: true },
  { label: "Coming Soon", value: "COMING+SOON", default: true },
  { label: "Active Under Contract", value: "ACTIVE+UNDER+CONTRACT", default: false },
  { label: "Pending", value: "PENDING", default: false },
];

const PRICE_MIN = 0;
const PRICE_MAX = 15000000;
const PRICE_STEP = 50000;

// Square footage options for IDX sqft parameter
const SQFT_OPTIONS = [
  { label: "Any", value: "" },
  { label: "500+", value: "500" },
  { label: "750+", value: "750" },
  { label: "1,000+", value: "1000" },
  { label: "1,250+", value: "1250" },
  { label: "1,500+", value: "1500" },
  { label: "1,750+", value: "1750" },
  { label: "2,000+", value: "2000" },
  { label: "2,500+", value: "2500" },
  { label: "3,000+", value: "3000" },
  { label: "4,000+", value: "4000" },
  { label: "5,000+", value: "5000" },
];

// Year built options
const YEAR_OPTIONS = [
  { label: "Any", value: "" },
  { label: "2020+", value: "2020" },
  { label: "2015+", value: "2015" },
  { label: "2010+", value: "2010" },
  { label: "2005+", value: "2005" },
  { label: "2000+", value: "2000" },
  { label: "1990+", value: "1990" },
  { label: "1980+", value: "1980" },
  { label: "1970+", value: "1970" },
];

// Lot size / acres options
const ACRES_OPTIONS = [
  { label: "Any", value: "" },
  { label: "0.25+ acres", value: "0.25" },
  { label: "0.5+ acres", value: "0.5" },
  { label: "1+ acre", value: "1" },
  { label: "2+ acres", value: "2" },
  { label: "5+ acres", value: "5" },
  { label: "10+ acres", value: "10" },
];

// Days on market options
const DOM_OPTIONS = [
  { label: "Any", value: "" },
  { label: "1 day", value: "1" },
  { label: "7 days", value: "7" },
  { label: "14 days", value: "14" },
  { label: "30 days", value: "30" },
  { label: "60 days", value: "60" },
  { label: "90 days", value: "90" },
];

const formatPrice = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value}`;
};

/**
 * Build IDX Broker URL with all filter parameters
 */
function buildIDXUrl(params: {
  locations: LocationSelection[];
  propertyType: string;
  statuses: string[];
  priceRange: [number, number];
  beds: string;
  baths: string;
  sqft: string;
  yearBuilt: string;
  acres: string;
  keywords: string;
  dom: string;
  priceReduced: boolean;
  newConstruction: boolean;
  hasGarage: boolean;
  hasPool: boolean;
  virtualTour: boolean;
}): string {
  const urlParams = new URLSearchParams();
  
  // Always required
  urlParams.append("idxID", IDX_ID);
  
  // Status - always include (defaults to Active + Coming Soon)
  params.statuses.forEach((status) => {
    urlParams.append("a_propStatus[]", status);
  });
  
  // Location (ZIP codes) - combine all selected locations
  const allZips = params.locations.flatMap((loc) => loc.zips);
  if (allZips.length > 0) {
    urlParams.append("ccz", "zip");
    allZips.forEach((zip) => {
      urlParams.append("zipcode[]", zip);
    });
  }
  
  // Property type
  if (params.propertyType) {
    urlParams.append("pt", params.propertyType);
  }
  
  // Price range
  if (params.priceRange[0] > PRICE_MIN) {
    urlParams.append("lp", params.priceRange[0].toString());
  }
  if (params.priceRange[1] < PRICE_MAX) {
    urlParams.append("hp", params.priceRange[1].toString());
  }
  
  // Beds
  if (params.beds) {
    urlParams.append("bd", params.beds);
  }
  
  // Baths
  if (params.baths) {
    urlParams.append("ba", params.baths);
  }
  
  // Square footage
  if (params.sqft) {
    urlParams.append("sqft", params.sqft);
  }
  
  // Year built (min)
  if (params.yearBuilt) {
    urlParams.append("a_yearBuilt", params.yearBuilt);
  }
  
  // Lot size / acres
  if (params.acres) {
    urlParams.append("acres", params.acres);
  }
  
  // Keywords - handle pool separately
  const keywordParts: string[] = [];
  if (params.keywords) {
    keywordParts.push(params.keywords);
  }
  if (params.hasPool) {
    keywordParts.push("pool");
  }
  if (keywordParts.length > 0) {
    urlParams.append("remarksKeyword", keywordParts.join(" "));
  }
  
  // Days on market
  if (params.dom) {
    urlParams.append("dom", params.dom);
  }
  
  // Price reduced
  if (params.priceReduced) {
    urlParams.append("pricereduced", "1");
  }
  
  // New construction
  if (params.newConstruction) {
    urlParams.append("a_newConstruction", "1");
  }
  
  // Has garage
  if (params.hasGarage) {
    urlParams.append("a_garage", "1");
  }
  
  // Virtual tour
  if (params.virtualTour) {
    urlParams.append("virtualTour", "1");
  }
  
  return `${IDX_BASE_URL}?${urlParams.toString()}`;
}

const SearchBar = ({ variant = "hero", className = "" }: SearchBarProps) => {
  // Location state (typeahead)
  const [locations, setLocations] = useState<LocationSelection[]>([]);
  const [propertyType, setPropertyType] = useState("");
  
  // Status multi-select (default: Active + Coming Soon)
  const [statuses, setStatuses] = useState<string[]>(
    STATUS_OPTIONS.filter((s) => s.default).map((s) => s.value)
  );
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  
  // Price range state
  const [priceRange, setPriceRange] = useState<[number, number]>([PRICE_MIN, PRICE_MAX]);
  const [pricePopoverOpen, setPricePopoverOpen] = useState(false);
  
  // Beds & Baths state
  const [beds, setBeds] = useState("");
  const [baths, setBaths] = useState("");
  
  // Advanced filters state
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [sqft, setSqft] = useState("");
  const [yearBuilt, setYearBuilt] = useState("");
  const [acres, setAcres] = useState("");
  const [keywords, setKeywords] = useState("");
  const [dom, setDom] = useState("");
  const [priceReduced, setPriceReduced] = useState(false);
  const [newConstruction, setNewConstruction] = useState(false);
  const [hasGarage, setHasGarage] = useState(false);
  const [hasPool, setHasPool] = useState(false);
  const [virtualTour, setVirtualTour] = useState(false);

  // Calculate active advanced filters count
  const advancedFiltersCount = [
    sqft !== "",
    yearBuilt !== "",
    acres !== "",
    keywords.length > 0,
    dom !== "",
    priceReduced,
    newConstruction,
    hasGarage,
    hasPool,
    virtualTour,
  ].filter(Boolean).length;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    const url = buildIDXUrl({
      locations,
      propertyType,
      statuses,
      priceRange,
      beds,
      baths,
      sqft,
      yearBuilt,
      acres,
      keywords,
      dom,
      priceReduced,
      newConstruction,
      hasGarage,
      hasPool,
      virtualTour,
    });
    
    trackGA4Event('idx_search', {
      event_category: 'lead_action',
      search_query: locations.join(', ') || 'all',
      property_type: propertyType,
      price_min: priceRange[0],
      price_max: priceRange[1],
      beds,
      baths,
    });

    // Redirect to IDX Broker
    window.location.href = url;
  };

  const getPriceDisplayText = () => {
    const minSet = priceRange[0] > PRICE_MIN;
    const maxSet = priceRange[1] < PRICE_MAX;
    if (!minSet && !maxSet) return "Any price";
    if (minSet && maxSet) return `${formatPrice(priceRange[0])} - ${formatPrice(priceRange[1])}`;
    if (minSet) return `${formatPrice(priceRange[0])}+`;
    return `Up to ${formatPrice(priceRange[1])}`;
  };

  const getStatusDisplayText = () => {
    if (statuses.length === 0) return "Select status";
    if (statuses.length === STATUS_OPTIONS.length) return "All statuses";
    if (statuses.length === 2 && statuses.includes("ACTIVE") && statuses.includes("COMING+SOON")) {
      return "Active & Coming Soon";
    }
    return `${statuses.length} selected`;
  };

  const toggleStatus = (value: string) => {
    setStatuses((prev) =>
      prev.includes(value)
        ? prev.filter((s) => s !== value)
        : [...prev, value]
    );
  };

  const clearAdvancedFilters = () => {
    setSqft("");
    setYearBuilt("");
    setAcres("");
    setKeywords("");
    setDom("");
    setPriceReduced(false);
    setNewConstruction(false);
    setHasGarage(false);
    setHasPool(false);
    setVirtualTour(false);
  };

  if (variant === "compact") {
    return (
      <form onSubmit={handleSearch} className={`flex gap-2 ${className}`}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by city, address, or ZIP..."
            className="pl-9"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const value = (e.target as HTMLInputElement).value;
                if (value) {
                  // Quick redirect with just the search term
                  const url = `${IDX_BASE_URL}?idxID=${IDX_ID}&a_propStatus[]=ACTIVE&a_propStatus[]=COMING+SOON`;
                  window.location.href = url;
                }
              }
            }}
          />
        </div>
        <Button type="submit">Search</Button>
      </form>
    );
  }

  return (
    <form
      onSubmit={handleSearch}
      className={`bg-background rounded-lg shadow-xl p-4 md:p-6 ${className}`}
    >
      {/* Row 1: Location, Property Type, Status, Price Range */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Location Typeahead */}
        <div className="md:col-span-1">
          <label className="text-sm font-medium mb-2 block">Location</label>
          <LocationTypeahead
            value={locations}
            onChange={setLocations}
            placeholder="City, town, or ZIP"
          />
        </div>

        {/* Property Type */}
        <div>
          <label className="text-sm font-medium mb-2 block">Property Type</label>
          <Select value={propertyType} onValueChange={setPropertyType}>
            <SelectTrigger className="h-12">
              <SelectValue placeholder="Any type" />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((type) => (
                <SelectItem key={type.value || "any"} value={type.value || "any"}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Status Multi-Select */}
        <div>
          <label className="text-sm font-medium mb-2 block">Status</label>
          <Popover open={statusPopoverOpen} onOpenChange={setStatusPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                type="button"
                className="w-full h-12 justify-between font-normal"
              >
                <span className="truncate">{getStatusDisplayText()}</span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-3 bg-popover" align="start">
              <div className="space-y-2">
                {STATUS_OPTIONS.map((status) => (
                  <div key={status.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`status-${status.value}`}
                      checked={statuses.includes(status.value)}
                      onCheckedChange={() => toggleStatus(status.value)}
                    />
                    <label
                      htmlFor={`status-${status.value}`}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {status.label}
                    </label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Price Range - Slider Popover */}
        <div>
          <label className="text-sm font-medium mb-2 block">Price Range</label>
          <Popover open={pricePopoverOpen} onOpenChange={setPricePopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                type="button"
                className="w-full h-12 justify-between font-normal"
              >
                <span className="truncate">{getPriceDisplayText()}</span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4 bg-popover" align="start">
              <div className="space-y-4">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{formatPrice(priceRange[0])}</span>
                  <span>{formatPrice(priceRange[1])}</span>
                </div>
                <Slider
                  value={priceRange}
                  onValueChange={(value) => setPriceRange(value as [number, number])}
                  min={PRICE_MIN}
                  max={PRICE_MAX}
                  step={PRICE_STEP}
                  className="w-full"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Min Price</Label>
                    <Input
                      type="number"
                      value={priceRange[0]}
                      onChange={(e) => {
                        const val = Math.max(PRICE_MIN, Math.min(Number(e.target.value), priceRange[1]));
                        setPriceRange([val, priceRange[1]]);
                      }}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Max Price</Label>
                    <Input
                      type="number"
                      value={priceRange[1]}
                      onChange={(e) => {
                        const val = Math.min(PRICE_MAX, Math.max(Number(e.target.value), priceRange[0]));
                        setPriceRange([priceRange[0], val]);
                      }}
                      className="h-9"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setPriceRange([PRICE_MIN, PRICE_MAX])}
                >
                  Reset
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Row 2: Beds, Baths, More Filters */}
      <div className="grid grid-cols-3 md:grid-cols-3 gap-3 mt-2">
        {/* Beds */}
        <Select value={beds} onValueChange={setBeds}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Beds" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any Beds</SelectItem>
            <SelectItem value="1">1+ Beds</SelectItem>
            <SelectItem value="2">2+ Beds</SelectItem>
            <SelectItem value="3">3+ Beds</SelectItem>
            <SelectItem value="4">4+ Beds</SelectItem>
            <SelectItem value="5">5+ Beds</SelectItem>
          </SelectContent>
        </Select>

        {/* Baths */}
        <Select value={baths} onValueChange={setBaths}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Baths" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any Baths</SelectItem>
            <SelectItem value="1">1+ Baths</SelectItem>
            <SelectItem value="2">2+ Baths</SelectItem>
            <SelectItem value="3">3+ Baths</SelectItem>
            <SelectItem value="4">4+ Baths</SelectItem>
          </SelectContent>
        </Select>

        {/* More Filters Button */}
        <Sheet open={advancedFiltersOpen} onOpenChange={setAdvancedFiltersOpen}>
          <SheetTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="h-8 w-full text-xs px-3 gap-1.5"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              More
              {advancedFiltersCount > 0 && (
                <Badge variant="secondary" className="h-4 w-4 p-0 text-[10px] flex items-center justify-center rounded-full">
                  {advancedFiltersCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-[320px] sm:w-[400px] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Advanced Filters</SheetTitle>
            </SheetHeader>
            <div className="space-y-6 mt-6">
              {/* Min Square Feet */}
              <div>
                <Label className="text-sm font-medium">Min Square Feet</Label>
                <Select value={sqft} onValueChange={setSqft}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    {SQFT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value || "any"} value={opt.value || "any"}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Year Built (Min) */}
              <div>
                <Label className="text-sm font-medium">Year Built (Min)</Label>
                <Select value={yearBuilt} onValueChange={setYearBuilt}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    {YEAR_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value || "any"} value={opt.value || "any"}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Lot Size / Acres */}
              <div>
                <Label className="text-sm font-medium">Lot Size (Acres)</Label>
                <Select value={acres} onValueChange={setAcres}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACRES_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value || "any"} value={opt.value || "any"}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Keywords */}
              <div>
                <Label className="text-sm font-medium">Keywords</Label>
                <Input
                  placeholder="Search descriptions..."
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  className="mt-2"
                />
              </div>

              {/* Max Days on Market */}
              <div>
                <Label className="text-sm font-medium">Max Days on Market</Label>
                <Select value={dom} onValueChange={setDom}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOM_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value || "any"} value={opt.value || "any"}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Checkbox Filters */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Features</Label>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="price-reduced"
                    checked={priceReduced}
                    onCheckedChange={(checked) => setPriceReduced(checked === true)}
                  />
                  <label htmlFor="price-reduced" className="text-sm cursor-pointer">
                    Price Reduced
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="new-construction"
                    checked={newConstruction}
                    onCheckedChange={(checked) => setNewConstruction(checked === true)}
                  />
                  <label htmlFor="new-construction" className="text-sm cursor-pointer">
                    New Construction
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="has-garage"
                    checked={hasGarage}
                    onCheckedChange={(checked) => setHasGarage(checked === true)}
                  />
                  <label htmlFor="has-garage" className="text-sm cursor-pointer">
                    Has Garage
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="has-pool"
                    checked={hasPool}
                    onCheckedChange={(checked) => setHasPool(checked === true)}
                  />
                  <label htmlFor="has-pool" className="text-sm cursor-pointer">
                    Has Pool
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="virtual-tour"
                    checked={virtualTour}
                    onCheckedChange={(checked) => setVirtualTour(checked === true)}
                  />
                  <label htmlFor="virtual-tour" className="text-sm cursor-pointer">
                    Virtual Tour Available
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={clearAdvancedFilters}
                >
                  Clear All
                </Button>
                <Button
                  type="button"
                  className="flex-1"
                  onClick={() => setAdvancedFiltersOpen(false)}
                >
                  Apply
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <Button type="submit" size="lg" className="w-full mt-4 h-12">
        <Search className="mr-2 h-5 w-5" />
        Search Properties
      </Button>
    </form>
  );
};

export default SearchBar;
